import React, { useRef, useCallback, useState, useEffect, useMemo } from "react";
import { Flex, FlexLeftCol, Colors } from "styles/global-style";
import Input from "components/Input";
import Textarea from "components/Textarea";
import PillDropdown from "components/PillDropdown";
import useKey from "utils/KeyListener";

import trashcan from "imgs/trashcan.png";

export default function EditTask({ submitTask, deleteTask, editingTask, users, usersForDisplay, subEpics, onSubEpicSelect, supabase, user }) {
  const titleRef = useRef();
  const ownerRef = useRef();
  const estimateRef = useRef();
  const descriptionRef = useRef();
  const subEpicRef = useRef();
  const [isEditingSubEpic, setIsEditingSubEpic] = useState(false);
  const [epicSubEpicInfo, setEpicSubEpicInfo] = useState({ epic: null, subEpic: null });
  const [subEpicHovered, setSubEpicHovered] = useState(false);

  useKey("Enter", false, (e) => {
    focusNext(e);
  });

  useKey("Enter", true, () => {
    submit();
  });

  const focusNext = useCallback(
    (e) => {
      if (e.target === titleRef.current) {
        ownerRef.current.focus();
      } else if (e.target === ownerRef.current.getRef()) {
        estimateRef.current.focus();
      } else if (e.target === estimateRef.current.getRef()) {
        descriptionRef.current.focus();
      }

      if (e.target !== descriptionRef.current) e.preventDefault();
    },
    [titleRef, ownerRef, estimateRef, descriptionRef]
  );

  const submit = useCallback(
    (props, keepOpen) => {
      submitTask(
        {
          id: editingTask.task.id,
          title: titleRef.current.value.trim(),
          description: descriptionRef.current?.value?.trim() || "",
          estimate: props?.estimate
            ? props.estimate === "remove"
              ? null
              : typeof props.estimate === "number"
              ? props.estimate
              : editingTask.task.estimate
            : editingTask.task.estimate,
          person_id: props?.ownerId
            ? props.ownerId === "remove"
              ? null
              : typeof props.ownerId === "number"
              ? props.ownerId
              : editingTask.task.owner_id
            : editingTask.task.owner_id,
          parent_id: props?.parentId !== undefined
            ? props.parentId === "remove"
              ? null
              : props.parentId
            : editingTask.task.parent_id,
        },
        keepOpen
      );
    },
    [submitTask, editingTask, titleRef]
  );

  const removeTask = useCallback(() => {
    if (deleteTask && editingTask.task.id > -1) {
      deleteTask(editingTask.task.id);
    }
  }, [deleteTask, editingTask]);

  // Reset edit mode when task changes
  useEffect(() => {
    setIsEditingSubEpic(false);
  }, [editingTask?.task?.id]);

  // Fetch epic and sub-epic info when parent_id exists
  useEffect(() => {
    async function fetchEpicSubEpicInfo() {
      if (!supabase || !editingTask?.task?.parent_id) {
        setEpicSubEpicInfo({ epic: null, subEpic: null });
        return;
      }

      // Fetch the sub-epic
      const { data: subEpicData } = await supabase
        .from("work")
        .select("id, title, parent_id")
        .eq("id", editingTask.task.parent_id)
        .eq("type", "subepic")
        .single();

      if (subEpicData && subEpicData.parent_id) {
        // Fetch the epic (parent of sub-epic)
        const { data: epicData } = await supabase
          .from("work")
          .select("id, title")
          .eq("id", subEpicData.parent_id)
          .eq("type", "epic")
          .single();

        setEpicSubEpicInfo({
          epic: epicData,
          subEpic: subEpicData
        });
      } else if (subEpicData) {
        setEpicSubEpicInfo({
          epic: null,
          subEpic: subEpicData
        });
      } else {
        setEpicSubEpicInfo({ epic: null, subEpic: null });
      }
    }

    if (editingTask?.task?.parent_id && !isEditingSubEpic) {
      fetchEpicSubEpicInfo();
    }
  }, [supabase, editingTask?.task?.parent_id, isEditingSubEpic]);

  return (
    <FlexLeftCol gap={4} style={{ position: 'relative', minHeight: '100%' }}>
      <Flex style={{ position: 'relative' }}>
        {/* Sub-epic assignment pill or view */}
        <div 
          style={{ position: 'absolute', top: -16, left: 0, zIndex: 10 }}
          onMouseEnter={() => setSubEpicHovered(true)}
          onMouseLeave={() => setSubEpicHovered(false)}
        >
          {isEditingSubEpic || !editingTask?.task?.parent_id ? (
            <PillDropdown
              ref={subEpicRef}
              list={subEpics?.map((subEpic) => ({
                value: subEpic.title,
                id: subEpic.id,
              })) || []}
              selectedId={editingTask?.task?.parent_id || null}
              placeholder={"Parent component"}
              onSelect={(item) => {
                setIsEditingSubEpic(false);
                if (onSubEpicSelect) {
                  onSubEpicSelect(item.id);
                } else {
                  submit({ parentId: item.id }, true);
                }
              }}
              onClear={() => {
                setIsEditingSubEpic(false);
                if (onSubEpicSelect) {
                  onSubEpicSelect(null);
                } else {
                  submit({ parentId: "remove" }, true);
                }
              }}
              pillStyle={{
                background: "transparent",
                paddingLeft: 0,
                color: "gray",
                borderRadius: 4,
                border: "none",
                textAlign: "left",
                minWidth: 120,
              }}
            />
          ) : (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                cursor: 'pointer',
                color: 'gray',
                fontSize: 11,
                minWidth: 120,
              }}
              onClick={() => setIsEditingSubEpic(true)}
            >
              <span>
                {epicSubEpicInfo.epic && epicSubEpicInfo.subEpic
                  ? `${epicSubEpicInfo.epic.title}: ${epicSubEpicInfo.subEpic.title}`
                  : epicSubEpicInfo.subEpic
                  ? epicSubEpicInfo.subEpic.title
                  : 'Parent component'}
              </span>
              <span
                className="edit-pen-icon"
                style={{
                  opacity: subEpicHovered ? 1 : 0,
                  transition: 'opacity 0.2s',
                  display: 'inline-flex',
                  alignItems: 'center',
                  height: 14,
                  width: 14,
                }}
              >
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14.7 3.29a1 1 0 0 1 1.42 0l.59.59a1 1 0 0 1 0 1.42l-9.3 9.3-2.12.7.7-2.12 9.3-9.3zM3 17h14v2H3v-2z" fill="currentColor"/>
                </svg>
              </span>
            </div>
          )}
        </div>

        <Input
          ref={titleRef}
          placeholder="Task title here..."
          autoFocus
          left
          value={editingTask?.task?.title}
          noborder
          customStyle={{ padding: 0, border: "none", outline: "none", fontSize: 28, fontWeight: "bold" }}
          onChangeDelay={() => {
            submit(null, true);
          }}
        />
      </Flex>

      {editingTask.task.id > -1 && (
        <img alt="delete" src={trashcan} style={{ position: "absolute", height: 14, bottom: -10, right: -10, opacity: 0.7, cursor: "pointer", zIndex: 10, filter: "brightness(0) saturate(100%) invert(27%) sepia(95%) saturate(7472%) hue-rotate(353deg) brightness(91%) contrast(89%)" }} onClick={() => removeTask()} />
      )}

      <Flex gap={6} style={{ marginBottom: 20 }}>
        <PillDropdown
          ref={ownerRef}
          list={(users || []).map((val) => ({
            value: !val.lastname ? val.firstname : val.firstname + " " + val.lastname.charAt(0),
            id: val.id,
          }))}
          selectedId={editingTask?.task?.owner_id || null}
          selectedLabelOverride={useMemo(() => {
            const ownerId = editingTask?.task?.owner_id;
            if (!ownerId || (users || []).some((u) => u.id === ownerId)) return undefined;
            const extra = usersForDisplay?.find((u) => u.id === ownerId);
            return extra ? (!extra.lastname ? extra.firstname : extra.firstname + " " + extra.lastname.charAt(0)) : undefined;
          }, [editingTask?.task?.owner_id, users, usersForDisplay])}
          placeholder={"Owner"}
          onSelect={(item) => {
            submit({ ownerId: item.id }, true);
          }}
          onClear={() => {
            submit({ ownerId: "remove" }, true);
          }}
          pillStyle={{
            background: Colors.GREEN,
            color: "white",
            border: "1px solid " + Colors.GREEN,
            borderRadius: 18,
          }}
        />
        <PillDropdown
          ref={estimateRef}
          hasPrefixNumber={true}
          prefixNumber={
            editingTask?.task?.estimate
              ? editingTask?.task?.estimate <= 8 || editingTask?.task?.estimate % 8 !== 0
                ? editingTask?.task?.estimate
                : editingTask?.task?.estimate / 8
              : null
          }
          list={[{ value: "days", id: "days" }, { value: "hrs", id: "hrs" }]}
          selectedId={editingTask?.task?.estimate ? (editingTask?.task?.estimate > 8 && editingTask?.task?.estimate % 8 === 0 ? "days" : "hrs") : null}
          placeholder={"Estimate"}
          onSelect={(item, numberValue) => {
            // numberValue is passed from PillDropdown when prefixNumber mode is active
            const number = numberValue || (editingTask?.task?.estimate
              ? (editingTask?.task?.estimate <= 8 || editingTask?.task?.estimate % 8 !== 0
                ? editingTask?.task?.estimate
                : editingTask?.task?.estimate / 8)
              : 0) || 0;
            // Always store estimate in hrs. I.e. 2 days = 2 * 8 hrs.
            const _estimate = item.id === "hrs" ? number : number * 8;
            submit({ estimate: _estimate }, true);
          }}
          onClear={() => {
            submit({ estimate: "remove" }, true);
          }}
          pillStyle={{
            background: "transparent",
            color: "gray",
            borderRadius: 4,
          }}
        />
      </Flex>

      <Textarea
        ref={descriptionRef}
        placeholder="Task details goes here..."
        onChangeDelay={() => {
          submit(null, true);
        }}
        left
        white
        value={editingTask?.task?.description}
        customStyle={{
          color: '#111',
          border: 'none',
          fontFamily: 'inherit',
          fontSize: 13,
          fontWeight: 'normal',
          background: 'transparent',
          textAlign: 'left',
          boxShadow: 'none',
          outline: 'none',
          padding: 0,
          margin: 0,
          resize: 'none',
          minHeight: 50
        }}
        className="modal-notes-dark-placeholder"
      />


      {/*<Flex>
        <Button text="Get AI estimate" callback={getGpt} color={"purple"} width={100} />
        {gpt && <div style={{ color: "black" }}>{gpt}</div>}
      </Flex>*/}
    </FlexLeftCol>
  );
}
